import { Text } from '@chakra-ui/react';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePatchTenantAsAppUser } from '@services/query-services/tenants.query-service';
import { useUserStore } from '@store/app.store';
import FullPageLoader from '@components/general/loader/full-page-loader.component';
import { registerWithBackend } from '@services/zoom.service';
import { useStyledToast } from '@util/hooks';
import * as Sentry from '@sentry/react';

function getCodeFromQueryString(s) {
  const params = new URLSearchParams(s);
  return params.get('code');
}

async function authorize(code, patchTenantAsAppUser, userStore, tenantId, toast, navigate) {
  try {
    const user = userStore?.user;
    const response = await registerWithBackend(code);
    patchTenantAsAppUser(
      { id: tenantId, recall_config: { zoom_oauth_credential_id: response.id } },
      {
        onSuccess: () => {
          userStore.refreshSessionTenant(tenantId);
          toast({
            title: 'Zoom connected successfully.',
            status: 'success'
          });

          if (user.coming_from === 'onboard') {
            setTimeout(() => {
              window.close();
            }, 100);
          }

          navigate('/agenda');
        },
        onError: (err) => {
          throw new Error(err);
        }
      }
    );
  } catch (e) {
    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'zoom_auth_error');
      scope.setContext('zoom_auth', { hasCode: !!code, tenantId });
      scope.setExtra('error_message', 'Error while authorizing Zoom with backend');
      Sentry.captureException(e);
    });
    toast({
      title: 'Something went wrong. You might already have Zoom connected.',
      status: 'error'
    });
  }
}

function ZoomCallbackPage() {
  const { mutateAsync: patchTenantAsAppUser } = usePatchTenantAsAppUser();
  const userStore = useUserStore();
  const user = userStore.user;

  const { search } = useLocation();
  const code = getCodeFromQueryString(search);
  const navigate = useNavigate();
  const toast = useStyledToast();

  useEffect(() => {
    if (code && user) {
      authorize(code, patchTenantAsAppUser, userStore, user.tenants[0].id, toast, navigate);
    }
  }, [code, user]);

  return (
    <FullPageLoader>
      <Text>Connecting Your Zoom Account...</Text>
    </FullPageLoader>
  );
}

export default ZoomCallbackPage;
